/* eslint-disable no-param-reassign */
import Vue from 'vue/dist/vue.esm';
import Vuex from 'vuex';
import axios from 'axios';

Vue.use(Vuex);

const csrfMetaTag = document.querySelector('meta[name=csrf-token]');

const handleError = (response) => {
  if (response.code === 'ECONNABORTED') {
    store.dispatch(
      'writeNotification',
      {
        level: 'error',
        message: 'Connection timed out or failed, try again later',
        errors: null,
      },
    );
  }

  return response;
};

axios.defaults.headers.common['X-CSRF-Token'] = csrfMetaTag.getAttribute('content');
axios.defaults.headers.common['Content-Type'] = 'application/json';
axios.defaults.headers.common.Accept = 'application/json';
axios.defaults.timeout = 5000;
axios.interceptors.response.use(
  (response) => handleError(response),
);

const store = new Vuex.Store({
  state: {
    basket: {
      basket_products: [],
      locale: {
        labels: {},
        unit: '$',
        mask: '0,0.00',
      },
      notification: null,
      totals: {
        sub_total: 0,
        delivery: 0,
        total_discount: 0,
        total_vat: 0,
        grand_total: 0,
        discounts: [],
      },
    },
  },
  mutations: {
    load(state, basket) {
      state.basket = basket;
    },
    addBasketProductDelegates(state, basketProduct) {
      const quantity = parseInt(basketProduct.quantity, 10);

      const i = state.basket.basket_products.findIndex((e) => e.id === basketProduct.id);

      const delegateCount = quantity - parseInt(basketProduct.delegates.length, 10);

      for (let x = 0; x < delegateCount; x += 1) {
        state.basket.basket_products[i].delegates.push({ id: null, name: null });
      }
    },
    removeBasketProductDelegates(state, basketProduct) {
      const i = state.basket.basket_products.findIndex((e) => e.id === basketProduct.id);

      state.basket.basket_products[i].delegates = [];
    },
    updateBasketProductDelegates(state, basketProduct) {
      const i = state.basket.basket_products.findIndex((e) => e.id === basketProduct.id);

      state.basket.basket_products[i].delegates = basketProduct.delegates;
    },
    updateBasketProduct(state, basketProduct) {
      const i = state.basket.basket_products.findIndex((e) => e.id === basketProduct.id);

      state.basket.basket_products[i].quantity = basketProduct.quantity;
      state.basket.basket_products[i].total = basketProduct.total;

      const delegatesLength = state.basket.basket_products[i].delegates.length;
      const quantity = parseInt(basketProduct.quantity, 10);

      if (quantity < delegatesLength) {
        const count = delegatesLength - quantity;

        for (let x = 0; x < count; x += 1) {
          state.basket.basket_products[i].delegates.pop();
        }
      } else if (quantity > delegatesLength) {
        const count = quantity - delegatesLength;

        for (let x = 0; x < count; x += 1) {
          state.basket.basket_products[i].delegates.push({
            id: null,
            name: '',
          });
        }
      }
    },
    removeBasketProduct(state, id) {
      const currentBasketProduct = state.basket.basket_products.findIndex(
        (e) => e.id === id,
      );

      if (currentBasketProduct !== -1) {
        state.basket.basket_products.splice(currentBasketProduct, 1);
      }
    },
    updateTotals(state, totals) {
      state.basket.totals.sub_total = totals.sub_total;
      state.basket.totals.delivery = totals.delivery;
      state.basket.totals.total_discount = totals.total_discount;
      state.basket.totals.total_vat = totals.total_vat;
      state.basket.totals.grand_total = totals.grand_total;
      state.basket.totals.discounts = totals.discounts;
    },
    setNotification(state, notification) {
      state.basket.notification = notification;
    },
    clearNotification(state) {
      state.basket.notification = null;
    },
  },
  actions: {
    load(context) {
      axios.get('/shop/basket.json').then((response) => {
        context.commit('load', response.data.basket);
      });
    },
    updateBasketProductDelegates(context, basketProduct) {
      axios.patch(`/shop/basket_products/${basketProduct.id}/ticket_delegates`, {
        ticket_delegates: basketProduct.delegates,
      }).then((response) => {
        if (response.status === 200) {
          context.commit('updateBasketProductDelegates', basketProduct);
          context.commit('setNotification', response.data.notification);
        }
      });
    },
    addBasketProductDelegates(context, basketProduct) {
      context.commit('addBasketProductDelegates', basketProduct);
    },
    removeBasketProductDelegates(context, basketProduct) {
      axios
        .delete(`/shop/basket_products/${basketProduct.id}/ticket_delegates`)
        .then((response) => {
          if (response.status === 200) {
            context.commit('removeBasketProductDelegates', basketProduct);
            context.commit('setNotification', response.data.notification);
          }
        });
    },
    updateBasketProductQuantity(context, basketProduct) {
      axios.patch(`/shop/basket_products/${basketProduct.id}`, {
        basket_product: { quantity: basketProduct.quantity },
      }).then((response) => {
        if (response.status === 200) {
          context.commit('updateBasketProduct', response.data.basket_product);
          context.commit('updateBasketProductDelegates', basketProduct);
          context.commit('updateTotals', response.data.totals);
          context.commit('setNotification', response.data.notification);
        }
      });
    },
    removeBasketProduct(context, payload) {
      axios.delete(`/shop/basket_products/${payload.id}`).then((response) => {
        if (response.status === 200) {
          context.commit('removeBasketProduct', payload.id);
          context.commit('updateTotals', response.data.totals);
          context.commit('setNotification', response.data.notification);
        }
      });
    },
    addDiscountCode(context, discountCode) {
      return axios.post('/shop/basket_discount_codes', {
        basket_discount_code: { code: discountCode },
      }).then((response) => {
        if (response.status === 200) {
          context.commit('updateTotals', response.data.totals);
          context.commit('setNotification', response.data.notification);
          return true;
        }

        return false;
      });
    },
    writeNotification(context, payload) {
      context.commit('setNotification', payload);
    },
  },
  getters: {
    basketEmpty: (state) => state.basket.basket_products.length <= 0,
    basketProducts: (state) => state.basket.basket_products,
    basketProduct: (state) => (id) => state.basket.basket_products.find(
      (basketProduct) => basketProduct.id === id,
    ),
    discounts: (state) => state.basket.totals.discounts,
    grandTotal: (state) => state.basket.totals.grand_total,
    locale: (state) => state.basket.locale,
    notification: (state) => state.basket.notification,
    subTotal: (state) => state.basket.totals.sub_total,
    totalDelivery: (state) => state.basket.totals.delivery,
    totalDiscount: (state) => state.basket.totals.total_discount,
    totalVat: (state) => state.basket.totals.total_vat,
  },
});

export default store;
